import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/public/Navbar";
import { Footer } from "@/components/public/Footer";
import { WhatsAppButton } from "@/components/public/WhatsAppButton";
import { useSettings } from "@/context/SettingsContext";
import api, { apiError, fmtTime } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Upload, X, CalendarDays, Clock, CheckCircle2 } from "lucide-react";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DAYS = ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sá"];

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookingPage() {
  const settings = useSettings();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [monthDate, setMonthDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState("");
  const [availability, setAvailability] = useState(null);
  const [refImages, setRefImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", session_type: "", package_id: "",
    num_people: "1", time_slot: "", location: "", comments: "", accepted_policies: false,
  });

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/packages").then(({ data }) => {
      setPackages(data);
      const pkg = params.get("package");
      if (pkg && data.find((p) => p.id === pkg)) setForm((f) => ({ ...f, package_id: pkg }));
    });
    const sess = params.get("session");
    if (sess) setForm((f) => ({ ...f, session_type: sess }));
  }, [params]);

  useEffect(() => {
    if (!selectedDate) return;
    setForm((f) => ({ ...f, time_slot: "" }));
    api.get(`/bookings/availability?date=${selectedDate}`).then(({ data }) => setAvailability(data));
  }, [selectedDate]);

  const grid = useMemo(() => {
    const y = monthDate.getFullYear();
    const m = monthDate.getMonth();
    const first = new Date(y, m, 1).getDay();
    const days = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(y, m, d));
    return cells;
  }, [monthDate]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const atCurrentMonth = monthDate.getFullYear() === today.getFullYear() && monthDate.getMonth() === today.getMonth();

  const uploadRefs = async (files) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/bookings/upload-reference", fd);
        setRefImages((r) => [...r, { id: data.id, name: file.name, preview: URL.createObjectURL(file) }]);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.full_name || !form.phone || !form.email) return toast.error("Completa tus datos de contacto.");
    if (!form.session_type) return toast.error("Elige el tipo de sesión.");
    if (!selectedDate) return toast.error("Selecciona una fecha.");
    if (!form.time_slot) return toast.error("Selecciona un horario.");
    if (!form.accepted_policies) return toast.error("Debes aceptar las políticas.");
    setSubmitting(true);
    try {
      const pkg = packages.find((p) => p.id === form.package_id);
      const { data } = await api.post("/bookings", {
        ...form,
        date: selectedDate,
        package_name: pkg?.name || "",
        reference_images: refImages.map((r) => r.id),
      });
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const depositNote = settings?.deposit_note;

  if (done) {
    return (
      <div className="bg-cream min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 pt-40 pb-32 text-center">
          <div className="w-20 h-20 rounded-full bg-gold/15 flex items-center justify-center mx-auto">
            <CheckCircle2 size={44} className="text-gold" />
          </div>
          <h1 className="mt-8 font-serif text-4xl sm:text-5xl text-ink">¡Solicitud recibida!</h1>
          <p className="mt-4 text-ink-soft" data-testid="booking-success-message">
            Gracias {done.full_name}. Hemos recibido tu solicitud para el <strong>{done.date}</strong> a las <strong>{fmtTime(done.time_slot)}</strong>. Te enviamos un correo de confirmación y me pondré en contacto contigo para coordinar el depósito.
          </p>
          <div className="mt-8 inline-block bg-cream-muted border border-beige rounded-sm px-6 py-4 text-left">
            <p className="font-mono text-xs text-gold">Estado</p>
            <p className="text-ink font-medium">{done.status}</p>
          </div>
          <div className="mt-10">
            <button onClick={() => navigate("/")} className="px-8 py-4 rounded-full bg-ink text-cream font-medium hover:bg-gold hover:text-ink transition-all">
              Volver al inicio
            </button>
          </div>
        </div>
        <Footer />
        <WhatsAppButton />
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-32 sm:pt-40 pb-24">
        <div className="text-center max-w-2xl mx-auto">
          <p className="overline-label mb-4">Reserva tu sesión</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink">Reservar una sesión</h1>
          <p className="mt-4 text-ink-soft">Completa el formulario y elige tu fecha ideal. Confirmaré tu reserva a la brevedad.</p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* LEFT: details */}
          <div className="space-y-8">
            <div>
              <p className="overline-label mb-4">1 · Tipo de sesión</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`booking-session-${c.id}`}
                    onClick={() => setForm({ ...form, session_type: c.name })}
                    className={`px-3 py-2.5 rounded-sm text-sm border text-left transition-all ${
                      form.session_type === c.name ? "bg-ink text-cream border-ink" : "border-beige-dark hover:border-gold text-ink-soft"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              <input
                data-testid="booking-session-custom"
                value={form.session_type}
                onChange={(e) => setForm({ ...form, session_type: e.target.value })}
                placeholder="O escribe el tipo de sesión"
                className="mt-3 w-full bg-transparent border-b border-beige-dark py-2 text-sm focus:border-gold outline-none"
              />
            </div>

            <div>
              <p className="overline-label mb-4">2 · Paquete</p>
              <div className="space-y-2">
                {packages.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`booking-package-${p.id}`}
                    onClick={() => setForm({ ...form, package_id: p.id })}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-sm border text-left transition-all ${
                      form.package_id === p.id ? "bg-ink text-cream border-ink" : "border-beige-dark hover:border-gold"
                    }`}
                  >
                    <span>
                      <span className="font-serif text-lg block">{p.name}</span>
                      <span className={`text-xs ${form.package_id === p.id ? "text-gold-light" : "text-ink-muted"}`}>{p.price}</span>
                    </span>
                    {form.package_id === p.id && <Check size={18} className="text-gold" />}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="booking-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Nombre completo *" className="bg-transparent border-b border-beige-dark py-2.5 focus:border-gold outline-none" />
              <input data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Teléfono *" className="bg-transparent border-b border-beige-dark py-2.5 focus:border-gold outline-none" />
              <input data-testid="booking-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Correo electrónico *" className="bg-transparent border-b border-beige-dark py-2.5 focus:border-gold outline-none" />
              <input data-testid="booking-people" value={form.num_people} onChange={(e) => setForm({ ...form, num_people: e.target.value })} placeholder="Cantidad de personas" className="bg-transparent border-b border-beige-dark py-2.5 focus:border-gold outline-none" />
              <input data-testid="booking-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Locación (opcional)" className="sm:col-span-2 bg-transparent border-b border-beige-dark py-2.5 focus:border-gold outline-none" />
            </div>

            <textarea data-testid="booking-comments" value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} rows={3} placeholder="Cuéntame tu idea para la sesión" className="w-full bg-transparent border-b border-beige-dark py-2.5 focus:border-gold outline-none resize-none" />

            <div>
              <p className="overline-label mb-3">Imágenes de referencia (opcional)</p>
              <label data-testid="booking-ref-upload" className="flex items-center gap-2 cursor-pointer text-sm text-ink-soft hover:text-gold transition-colors">
                <Upload size={16} /> {uploading ? "Subiendo…" : "Subir imágenes"}
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => uploadRefs(e.target.files)} />
              </label>
              {refImages.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {refImages.map((r, i) => (
                    <div key={r.id} className="relative w-16 h-16 rounded-sm overflow-hidden border border-beige">
                      <img src={r.preview} alt={r.name} className="w-full h-full object-cover" />
                      <button onClick={() => setRefImages((x) => x.filter((_, idx) => idx !== i))} className="absolute top-0 right-0 bg-ink/70 text-white p-0.5"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: calendar + time */}
          <div className="lg:sticky lg:top-28 h-fit">
            <div className="bg-cream-muted border border-beige rounded-sm p-6">
              <p className="overline-label mb-4 flex items-center gap-2"><CalendarDays size={14} /> 3 · Elige la fecha</p>
              <div className="flex items-center justify-between mb-4">
                <button data-testid="cal-prev" disabled={atCurrentMonth} onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))} className={`p-1.5 rounded-full transition-colors ${atCurrentMonth ? "text-beige-dark cursor-not-allowed" : "hover:bg-beige text-ink"}`}><ChevronLeft size={18} /></button>
                <span className="font-serif text-xl text-ink">{MONTHS[monthDate.getMonth()]} {monthDate.getFullYear()}</span>
                <button data-testid="cal-next" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))} className="p-1.5 rounded-full hover:bg-beige transition-colors"><ChevronRight size={18} /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {DAYS.map((d) => <div key={d} className="text-xs font-mono text-ink-muted py-1">{d}</div>)}
                {grid.map((d, i) => {
                  if (!d) return <div key={i} />;
                  const disabled = d < today;
                  const ds = fmt(d);
                  const active = selectedDate === ds;
                  return (
                    <button
                      key={i}
                      data-testid={`cal-day-${ds}`}
                      disabled={disabled}
                      onClick={() => setSelectedDate(ds)}
                      className={`aspect-square rounded-full text-sm transition-all ${
                        active ? "bg-ink text-cream" : disabled ? "text-beige-dark cursor-not-allowed" : "text-ink hover:bg-gold/20"
                      }`}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>

              {selectedDate && (
                <div className="mt-6">
                  <p className="overline-label mb-3 flex items-center gap-2"><Clock size={14} /> 4 · Horario disponible</p>
                  {availability === null ? (
                    <p className="text-sm text-ink-muted">Cargando…</p>
                  ) : availability.available.length === 0 ? (
                    <p className="text-sm text-ink-muted" data-testid="no-slots">No hay horarios disponibles este día. Elige otra fecha.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {availability.all_slots.map((s) => {
                        const taken = !availability.available.includes(s);
                        return (
                          <button
                            key={s}
                            data-testid={`slot-${s}`}
                            disabled={taken}
                            onClick={() => setForm({ ...form, time_slot: s })}
                            className={`px-4 py-2 rounded-full text-sm border transition-all ${
                              form.time_slot === s ? "bg-gold text-ink border-gold" : taken ? "text-beige-dark border-beige line-through cursor-not-allowed" : "border-beige-dark hover:border-gold text-ink"
                            }`}
                          >
                            {fmtTime(s)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {depositNote && <p className="mt-4 text-xs text-ink-muted">{depositNote}</p>}

            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <input data-testid="booking-policies" type="checkbox" checked={form.accepted_policies} onChange={(e) => setForm({ ...form, accepted_policies: e.target.checked })} className="mt-1 accent-[#C5A059]" />
              <span className="text-sm text-ink-soft">Acepto las políticas de reserva y cancelación.</span>
            </label>

            <button
              data-testid="booking-submit"
              onClick={submit}
              disabled={submitting}
              className="mt-5 w-full px-6 py-4 rounded-full bg-ink text-cream font-medium hover:bg-gold hover:text-ink transition-all duration-300 disabled:opacity-60"
            >
              {submitting ? "Enviando solicitud…" : "Solicitar reserva"}
            </button>
          </div>
        </div>
      </div>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
